// 本地缩略图缓存（用于列表页加速）
// - 生成固定尺寸缩略图（默认 360x240，cover 裁剪）
// - 使用 Cache Storage + objectURL，刷新后仍可复用
// - 仅用于“同源/允许跨域读取”的图片；若图片不允许读取像素，会自动降级为原图 URL

const CACHE_NAME = 'thumbs-v1'
const memoryUrlMap = new Map() // key -> objectURL

function cacheKey(originalUrl, width, height) {
  return `${originalUrl}__w${width}__h${height}`
}

async function getCache() {
  if (!('caches' in window)) return null
  return await caches.open(CACHE_NAME)
}

function blobToObjectUrl(key, blob) {
  const existing = memoryUrlMap.get(key)
  if (existing) return existing
  const url = URL.createObjectURL(blob)
  memoryUrlMap.set(key, url)
  return url
}

async function loadImageBitmap(url) {
  // createImageBitmap 更快，但部分浏览器/场景可能不可用
  const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
  const blob = await res.blob()
  if ('createImageBitmap' in window) {
    return await createImageBitmap(blob)
  }
  // fallback: HTMLImageElement
  const img = await new Promise((resolve, reject) => {
    const el = new Image()
    el.crossOrigin = 'anonymous'
    el.onload = () => resolve(el)
    el.onerror = reject
    el.src = URL.createObjectURL(blob)
  })
  return img
}

function drawCoverToCanvas(img, targetW, targetH) {
  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no canvas context')

  const srcW = img.width
  const srcH = img.height
  if (!srcW || !srcH) throw new Error('invalid image size')

  // cover：按比例裁剪到目标比例
  const targetRatio = targetW / targetH
  const srcRatio = srcW / srcH

  let sx = 0
  let sy = 0
  let sWidth = srcW
  let sHeight = srcH

  if (srcRatio > targetRatio) {
    // 源图更宽，裁左右
    sWidth = Math.round(srcH * targetRatio)
    sx = Math.round((srcW - sWidth) / 2)
  } else {
    // 源图更高，裁上下
    sHeight = Math.round(srcW / targetRatio)
    sy = Math.round((srcH - sHeight) / 2)
  }

  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetW, targetH)
  return canvas
}

async function canvasToJpegBlob(canvas, quality = 0.72) {
  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality)
  })
  if (!blob) throw new Error('toBlob failed')
  return blob
}

export async function getThumbUrl(originalUrl, options = {}) {
  const width = options.width ?? 360
  const height = options.height ?? 240

  if (!originalUrl) return ''
  const key = cacheKey(originalUrl, width, height)

  // 1) 内存命中（最快）
  const mem = memoryUrlMap.get(key)
  if (mem) return mem

  // 2) Cache Storage 命中（刷新后仍在）
  try {
    const cache = await getCache()
    if (cache) {
      const match = await cache.match(key)
      if (match) {
        const blob = await match.blob()
        return blobToObjectUrl(key, blob)
      }
    }
  } catch {
    // 忽略缓存读取失败，继续生成
  }

  // 3) 生成缩略图并写入缓存
  try {
    const img = await loadImageBitmap(originalUrl)
    const canvas = drawCoverToCanvas(img, width, height)
    const blob = await canvasToJpegBlob(canvas)

    try {
      const cache = await getCache()
      if (cache) {
        await cache.put(key, new Response(blob, { headers: { 'Content-Type': 'image/jpeg' } }))
      }
    } catch {
      // 写入失败也不影响使用
    }

    return blobToObjectUrl(key, blob)
  } catch {
    // 跨域不允许读像素/生成失败：降级为原图
    return originalUrl
  }
}

export function revokeAllThumbObjectUrls() {
  for (const url of memoryUrlMap.values()) {
    try { URL.revokeObjectURL(url) } catch {
      // 忽略 revoke 失败（某些浏览器/异常状态下可能抛错）
    }
  }
  memoryUrlMap.clear()
}

